import { Modal, Col, Menu, Row, Space, Typography, Dropdown, message } from "antd";
import { useState } from "react";
import { Guid } from "guid-typescript";
import AssignmentGridView from "../components/assignments/AssignmentGridView";
import AssignmentFormDialog from "../components/assignments/AssignmentFormDialog";
import AssignRewardFormDialog from "../components/AssignRewardFormDialog";
import { AssignmentTarget } from "../components/assignments/AssignmentGridView.default";
import { SearchQuery } from "../code/search-query/search-query";
import {
  getCompaniesRewardsExport,
  getDepartmentsRewardsExport,
  getUsersRewardsExport,
} from "../repository/data.repository";
import Loader from "../controls/Loader";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getProblemMessage } from "../utils/http";

const exportFns: Record<AssignmentTarget, (input: any) => Promise<any>> = {
  company: getCompaniesRewardsExport,
  department: getDepartmentsRewardsExport,
  user: getUsersRewardsExport,
};

function AssignmentsPage() {
  const permissions = useSelector(authorizeSelectors.permissions);
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [selectedKey, setSelectedKey] = useState<AssignmentTarget>("company");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRecord, setDialogRecord] = useState<any>({});
  const [assignOpen, setAssignOpen] = useState(false);
  const [triggerView, setTriggerView] = useState(Guid.create().toString());

  const items = [
    { key: "company", label: "Company Rewards" },
    { key: "department", label: "Department Rewards" },
    { key: "user", label: "User Rewards" },
  ];

  function onSelect(e: { readonly key: string }) {
    setSelectedKey(e.key as AssignmentTarget);
  }

  function onSearch(query: SearchQuery): void {
    setSearchQuery(query);
  }

  function onAction(key: string, record: any): void {
    if (key === "delete") {
      setDialogRecord(record);
      setDialogOpen(true);
    }
  }

  function onFinish(): void {
    setDialogOpen(false);
    setDialogRecord({});
    setTriggerView(Guid.create().toString());
  }

  function onClose(): void {
    setDialogOpen(false);
    setDialogRecord({});
  }

  function onAssignFinish(): void {
    setAssignOpen(false);
    setTriggerView(Guid.create().toString());
  }

  function onAssignClose(): void {
    setAssignOpen(false);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading rewards export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });

    exportFns[selectedKey]({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => loading.destroy())
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Assignments
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("Rewards", "create")}

            className={`${!permissions.can("Rewards", "create") ? "disabled" : ""}`}
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("Rewards", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={() => setAssignOpen(true)}
          >
            Assign Reward
          </Dropdown.Button>
        </Space>
      </Row>
      <Row className="flex flex-auto flex-row flex-nowrap">
        <Col className="flex flex-col pb-4">
          <Menu
            className="ml-2 border-none bg-transparent"
            items={items}
            onSelect={onSelect}
            selectedKeys={[selectedKey]}
          />
        </Col>
        <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
          <AssignmentGridView
            key={selectedKey}
            variant="reward"
            target={selectedKey}
            trigger={triggerView}
            onAction={onAction}
            onSearch={onSearch}
          />
        </Col>
      </Row>
      <AssignmentFormDialog
        variant="reward"
        target={selectedKey}
        open={dialogOpen}
        initialValues={dialogRecord}
        onFinish={onFinish}
        onCancel={onClose}
      />
      <AssignRewardFormDialog
        open={assignOpen}
        onFinish={onAssignFinish}
        onCancel={onAssignClose}
      />
      {contextHolder}
    </Col>
  );
}

export default AssignmentsPage;
